import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Dimension,
  Dimensions,
  ListingPhoto,
  MarketingOrder,
  MarketingOrderService,
  PhotoArrayType,
  ProductCategory,
  ProductCode,
  ProductInstance,
  ProductKind,
  PromptDialogService,
  TemplateInstanceMetaData,
} from '@lc/core';
import { AspectRatios, CropRequest, ImageCropperNewDialogService, PhotoCropSettings } from '@lc/ui';
import { buildPlaceholderImage, PhotoViewModel } from '../photo-view-model';
import { PhotoGalleryDialogService } from '../../dialogs/photo-gallery-dialog/photo-gallery-dialog.service';
import { PhotoComponent } from '../photo-list/photo/photo.component';

@Component({
  selector: 'lc-product-photos',
  templateUrl: './product-photos.component.html',
  styleUrls: ['./product-photos.component.scss'],
  imports: [CdkDropListGroup, CdkDropList, CdkDrag, PhotoComponent],
})
export class ProductPhotosComponent implements OnChanges {
  private cropDialog = inject(ImageCropperNewDialogService);
  private galleryService = inject(PhotoGalleryDialogService);
  private marketingOrderService = inject(MarketingOrderService);
  private promptDialog = inject(PromptDialogService);

  @Input() productCode: string;
  @Input() marketingOrder: MarketingOrder;
  @Input() viewModels: PhotoViewModel[];
  @Input() photoType: string;
  @Input() hideHeadingAndInstructions = false;
  @Input() hidePageHeaders = false;
  @Input() readOnly = false;

  @Output() readonly updatePhotos = new EventEmitter<any>();

  productPhotos: PhotoViewModel[] = [];
  product: ProductInstance;
  isWebsite = false;
  isVideoService = false;
  templateInfo: TemplateInstanceMetaData;

  get showPageHeaders() {
    return !this.hidePageHeaders && this.productPhotos.some((viewModel) => Boolean(viewModel.pageDisplay));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!(changes.productCode || changes.marketingOrder || changes.viewModels || changes.readOnly)) return;
    if (!this.marketingOrder || !this.productCode || !this.viewModels) return;

    this.product = this.marketingOrder.getProduct(this.productCode);
    this.isWebsite = this.isWebsiteProduct(this.product);
    this.isVideoService = this.product?.kind === ProductKind.SERVICE
      && this.product?.category === ProductCategory.VIDEO;
    this.initializeProductPhotos();
  }

  private initializeProductPhotos() {
    this.templateInfo = this.product?.selectedTemplate?.templateInfo;

    let photos: ListingPhoto[];
    if (this.isComingSoonProduct()) {
      photos = this.viewModels.map((viewModel) => viewModel.photo);
    } else {
      const productPhotos = this.marketingOrder.getPhotosForProduct(this.productCode);
      const configuredPhotos = Array.isArray(productPhotos) ? productPhotos : productPhotos?.[this.photoType];
      photos = configuredPhotos?.length ? configuredPhotos : this.defaultSort(this.marketingOrder.photos);
    }

    const photoLimit = this.getPhotoLimit(photos.length);
    this.productPhotos = photos
      .slice(0, photoLimit)
      .map((photo) => new PhotoViewModel(this.clonePhoto(photo)));

    if (!this.isComingSoonProduct()) {
      while (this.productPhotos.length < photoLimit) {
        this.productPhotos.push(this.buildPlaceholderImage(this.productPhotos.length));
      }
    }

    this.productPhotos.forEach((viewModel, index) => this.setDisplayProperties(viewModel, index + 1));
    this.updateInUseIndex();
  }

  private setDisplayProperties(viewModel: PhotoViewModel, order: number) {
    const isDefault = viewModel.isDefault();
    const isPropertyWebsiteCarousel = this.isPropertyWebsiteCarousel();
    viewModel.showOrder = true;
    viewModel.showFavorite = isPropertyWebsiteCarousel;
    viewModel.useSmallIcons = true;
    viewModel.showSelected = false;
    viewModel.canFavorite = isPropertyWebsiteCarousel && !isDefault && !this.readOnly;
    viewModel.canReorder = false;
    viewModel.canDownload = false;
    viewModel.canSelect = false;
    viewModel.canHide = false;
    viewModel.canDelete = !isDefault && !this.readOnly;
    viewModel.canEdit = !isDefault
      && !this.readOnly
      && this.photoType !== PhotoArrayType.VIDEO
      && Boolean(this.getCropDimension(order - 1));
    viewModel.disablePortrait = this.product?.isVideoService();
    viewModel.iconPlacement = 'corner';
    viewModel.photo.order = order;
    viewModel.photo.hidden = false;

    const currentPageNumber = this.templateInfo?.photoInfo?.[order - 1]?.pageNumber;
    const previousPageNumber = this.templateInfo?.photoInfo?.[order - 2]?.pageNumber;
    viewModel.pageDisplay = currentPageNumber && currentPageNumber !== previousPageNumber
      ? `Page ${currentPageNumber}`
      : null;
  }

  private updateInUseIndex() {
    this.viewModels.forEach((viewModel) => {
      viewModel.inUseIndex = -1;
    });
    this.productPhotos.forEach((productViewModel, index) => {
      const orderViewModel = this.viewModels.find(
        (viewModel) => viewModel.photo._id === productViewModel.photo._id,
      );
      if (orderViewModel) orderViewModel.inUseIndex = index;
    });
  }

  onOpenGallery(index: number) {
    if (this.productPhotos[index]?.isDefault()) return;
    const photos = this.productPhotos.map((viewModel) => viewModel.photo);
    this.galleryService.open(
      this.marketingOrder.listing.formatAddress(),
      photos,
      index,
      this.marketingOrder,
      {
        photoViewModels: this.productPhotos,
        deletePhoto: (viewModel) => {
          const productPhoto = this.findProductPhoto(viewModel);
          if (productPhoto) this.onDeletePhoto(productPhoto);
        },
        editPhoto: async (viewModel, photoIndex) => {
          const productPhoto = this.findProductPhoto(viewModel);
          if (!productPhoto) return;
          await this.onCropPhoto(productPhoto, photoIndex);
          viewModel.photo.dimensions = productPhoto.photo.dimensions;
        },
        toggleFavorite: (viewModel) => this.onToggleFavorite(viewModel),
      },
    );
  }

  onDragAndDrop(event: CdkDragDrop<PhotoViewModel[]>) {
    if (this.readOnly) return;

    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) return;
      moveItemInArray(this.productPhotos, event.previousIndex, event.currentIndex);
    } else {
      const sourcePhoto = event.previousContainer.data[event.previousIndex];
      const targetIndex = Math.min(event.currentIndex, this.productPhotos.length - 1);
      const newPhoto = new PhotoViewModel(this.clonePhoto(sourcePhoto.photo));
      if (this.productPhotos.length) {
        this.replacePhoto(this.productPhotos[targetIndex], newPhoto);
      } else {
        this.productPhotos.push(newPhoto);
      }
    }

    this.productPhotos = [...this.productPhotos];
    this.executeUpdate();
  }

  async onCropPhoto(viewModel: PhotoViewModel, photoIndex: number) {
    const templateDimension = this.getCropDimension(photoIndex);
    if (!templateDimension) return;

    viewModel.photo.dimensions ||= {};
    const photoDimension = viewModel.photo.dimensions[viewModel.photo.order] || new Dimensions();
    photoDimension.templateDimensions = templateDimension;

    const cropSettings = new PhotoCropSettings(templateDimension.width / templateDimension.height);
    cropSettings.dimensions = photoDimension;
    const response = await this.cropDialog.openImageCropper(
      new CropRequest(viewModel.photo.uri, `Edit Photo ${viewModel.photo.order}`, cropSettings),
    );

    if (response?.croppedPhotos?.length) {
      const dimensions = response.croppedPhotos[0]?.dimensions || {};
      dimensions.templateDimensions = photoDimension.templateDimensions;
      viewModel.photo.dimensions[viewModel.photo.order] = dimensions;
      this.executeUpdate();
    }
  }

  async onToggleFavorite(viewModel: PhotoViewModel) {
    const favorite = !viewModel.photo.favorite;
    viewModel.photo.favorite = favorite;
    this.setFavorite(viewModel.photo._id, favorite);

    await this.marketingOrderService.updatePhoto(
      this.marketingOrder._id,
      viewModel.photo._id,
      viewModel.photo,
    ).catch((error) => {
      viewModel.photo.favorite = !favorite;
      this.setFavorite(viewModel.photo._id, !favorite);
      const message = error?.error?.error?.message ?? 'An error occurred favoriting this photo.';
      this.promptDialog.openPrompt('Error', message, 'Ok');
    });
  }

  onDeletePhoto(viewModel: PhotoViewModel) {
    viewModel.inUseIndex = null;
    if (this.isComingSoonProduct()) {
      this.productPhotos = this.productPhotos.filter((photo) => photo !== viewModel);
    } else {
      this.replacePhoto(viewModel, this.buildPlaceholderImage(viewModel.photo.order - 1));
    }
    this.executeUpdate();
  }

  onPortraitDisabled(viewModel: PhotoViewModel) {
    this.onDeletePhoto(viewModel);
  }

  private replacePhoto(oldPhoto: PhotoViewModel, newPhoto: PhotoViewModel) {
    const index = this.productPhotos.indexOf(oldPhoto);
    if (index >= 0) this.productPhotos.splice(index, 1, newPhoto);
  }

  private executeUpdate() {
    if (!this.isComingSoonProduct()) {
      this.productPhotos = this.productPhotos.slice(0, this.getPhotoLimit(this.productPhotos.length));
    }

    this.productPhotos.forEach((viewModel, index) => this.setDisplayProperties(viewModel, index + 1));

    const productPhotosMap = {};
    productPhotosMap[this.photoType] = this.productPhotos.map((viewModel) => viewModel.photo);
    this.updatePhotos.emit(productPhotosMap);
    this.updateInUseIndex();
  }

  private getPhotoLimit(fallback: number) {
    return this.templateInfo?.photoCount || this.product?.details?.maxPhotos || fallback;
  }

  private getCropDimension(photoIndex: number): Dimension | undefined {
    const metadata = this.templateInfo?.photoInfo?.[photoIndex];
    if (metadata) return new Dimension(metadata.width, metadata.height);
    if (this.isPropertyWebsiteCarousel()) return new Dimension(AspectRatios['2:1'], 1);
    return undefined;
  }

  private setFavorite(photoId: string, favorite: boolean) {
    [...this.productPhotos, ...this.viewModels]
      .filter((viewModel) => viewModel.photo._id === photoId)
      .forEach((viewModel) => {
        viewModel.photo.favorite = favorite;
      });
  }

  private findProductPhoto(viewModel: PhotoViewModel) {
    return this.productPhotos.find((productPhoto) => productPhoto.photo._id === viewModel.photo._id);
  }

  private isPropertyWebsiteCarousel() {
    return this.product?.code === ProductCode.PROPERTY_WEBSITE
      && this.photoType === PhotoArrayType.CAROUSEL;
  }

  private isComingSoonProduct() {
    return this.product?.code === ProductCode.COMING_SOON_MLS_ENTRY
      || this.product?.code === ProductCode.COMING_SOON_GOOGLE_AD;
  }

  private defaultSort(photos: ListingPhoto[]) {
    return photos
      .filter((photo) => photo.favorite)
      .sort((first, second) => first.order - second.order)
      .concat(
        photos
          .filter((photo) => !photo.favorite)
          .sort((first, second) => first.order - second.order),
      );
  }

  private buildPlaceholderImage(index: number): PhotoViewModel {
    const viewModel = new PhotoViewModel(buildPlaceholderImage(index));
    this.setDisplayProperties(viewModel, index + 1);
    return viewModel;
  }

  private clonePhoto(photo: ListingPhoto) {
    return new ListingPhoto(structuredClone(photo));
  }

  isWebsiteProduct(product: ProductInstance) {
    return product?.code === ProductCode.WEBSITE || product?.code === ProductCode.PROPERTY_WEBSITE;
  }
}
